/**
 * Pano metinleri: dört bölümün ana ekranı (`Dashboard.tsx`, CRM-A14, `docs/tasarim/dashboard.md`).
 *
 * Açılışta grafik yok, toplam yok, hoş geldiniz yok. İlk kutu o bölümün en yaşlı bekleyen işi,
 * her kuyruğun başlığında tek bir canlı sayı: yaş medyanı, saat cinsinden.
 *
 * Ekran hiçbir sayı üretmiyor; buradaki metinler yalnız uçtan gelen sayının etiketi. Kuyruk
 * tablolarının sütun adları da uçtan geliyor (satırın anahtarları), o yüzden `SUTUN_ADI`
 * karşılığı olmayan anahtarda **anahtarın kendisini** yazıyor: uydurulmuş bir karşılık, eksik
 * bir karşılıktan kötüdür.
 */

/**
 * Kuyruk tablolarının sütun adları.
 *
 * Anahtarlar `api/src/modules/dashboard/dashboard.service.ts` içindeki satır eşleyicilerinden
 * geliyor. Uç yeni bir alan gönderdiğinde ekran onu gizlemiyor, ham anahtarıyla yazıyor: eksik
 * karşılık görünür kalsın diye.
 */
export const SUTUN_ADI: Readonly<Record<string, string>> = {
  ageHours: 'Yaş (saat)',
  ageBucket: 'Yaş aralığı',
  /** Sipariş kapanış kuyruğunda: yaş çapasının gerçek bir durum geçişinden mi (`WorkflowTransition`)
   *  yoksa `Order.updatedAt`e düşerek mi geldiği (`dashboard.service.ts`, `readyQueue`). */
  anchorFromTransition: 'Zaman kaynağı: durum geçişi',
  amountMinor: 'Tutar',
  applicantName: 'Başvuran',
  applicantType: 'Başvuru türü',
  attempts: 'Deneme',
  blockedReason: 'Bloke sebebi',
  byClient: 'Müşteriden mi',
  campaignId: 'Kampanya',
  claimedCount: 'Talep edilen',
  code: 'Kod',
  costMinor: 'Maliyet',
  currencyCode: 'Para birimi',
  customer: 'Müşteri',
  customerId: 'Müşteri',
  customerName: 'Müşteri',
  decidedBy: 'Kararı veren',
  departureOn: 'Hareket',
  description: 'Açıklama',
  /** İK kuyruğu (30.08.2026): çalışanın adı, adresi ve rolü. */
  displayName: 'Ad',
  divergedCount: 'Ayrışan',
  email: 'E-posta',
  gates: 'Kapılar',
  id: 'Kayıt',
  item: 'Kalem',
  itemCount: 'Kalem sayısı',
  itemReference: 'Kalem referansı',
  itemType: 'Kalem türü',
  kind: 'Tür',
  lineCount: 'Satır sayısı',
  matchedCount: 'Eşleşen',
  maturesOn: 'Vade',
  method: 'Yöntem',
  missingCount: 'Eksik',
  nextStatus: 'Sonraki durum',
  openTaskCount: 'Açık görev',
  orderCode: 'Sipariş kodu',
  orderId: 'Sipariş',
  orderReference: 'Sipariş referansı',
  outcome: 'Sonuç',
  outstandingMinor: 'Açık bakiye',
  pendingRefundCount: 'Bekleyen iade',
  productCode: 'Ürün kodu',
  productId: 'Ürün',
  /** Değeri çevrilmiyor: dış sistemin (v3) döndürdüğü ham tanı metni, `Dashboard.tsx`te `code` olarak çiziliyor. */
  publishError: 'Yayım hatası',
  quantity: 'Adet',
  quoteCode: 'Teklif kodu',
  quoteId: 'Teklif',
  reason: 'Sebep',
  reconciliationStatus: 'Mutabakat durumu',
  recordedByName: 'Kaydeden',
  reference: 'Referans',
  requestedByName: 'Talep eden',
  reservationId: 'Rezervasyon',
  /** İK kuyruğu (30.08.2026). Hücredeki kod değeri `Dashboard.tsx` içinde `ROL_ADI` ile çevriliyor. */
  role: 'Rol',
  state: 'Durum',
  status: 'Durum',
  supplierCode: 'Tedarikçi kodu',
  supplierReference: 'Tedarikçi referansı',
  taskCount: 'Görev sayısı',
  taskStatuses: 'Görev durumları',
  totalMinor: 'Toplam',
  verifiableByMe: 'Doğrulayabilir miyim',
  versionNo: 'Sürüm',
};

export const PANO_METIN = {
  /** Uç bir hata gövdesi göndermediğinde yazılan cümle. */
  istekHatasi: 'İstek tamamlanamadı.',

  hucre: {
    evet: 'evet',
    hayir: 'hayır',
    /** Mutabakatın kapalı kapısı yoksa hücre boş kalmıyor, "kapı yok" diyor. */
    kapiYok: 'kapı yok',
  },

  kuyruk: {
    /** Ekranda bir tarih değil bir süre okunuyor: "61s" bekleyen işin yaşı. */
    saatEki: 's',
    yasYok: 'kayıt yok',
    medyanYas: 'medyan yaş:',
    yasOlculmuyor: 'yaş ölçülmüyor',
    enYasli: 'en yaşlı:',
    yirmiDortAlti: '< 24s:',
    yirmiDortKirkSekiz: '24-48s:',
    kirkSekizUstu: '> 48s:',
    bosBaslik: 'Bekleyen kayıt yok',
    bosTarif: 'Bu kuyruk bugün boş. Boş olduğu için ekrandan kaldırılmıyor.',
  },

  /**
   * Kuyruk satırının kendi eylemi.
   *
   * Ölçülen kusur: "Doğrulanmamış ödeme kanıtı" kuyruğundaki her satır `/siparisler/<id>`
   * adresine bağlanıyordu, o sayfada eylem düğmesi sayısı sıfırdı, ve finansçı geri dönüp aynı
   * siparişi 21 seçenekli listeden yeniden buluyordu: kalem başına 5 tık ve 2 sayfa geçişi.
   *
   * **Eylem kuyruğa taşındı, sayfaya değil.** İki seçenek vardı ve seçim gerekçeliydi: gidilen
   * sayfaya düğme koymak da tıkı 5'ten 2'ye indirirdi (bağlantı, sonra düğme) ama sayfa
   * geçişini kaldırmazdı, ve kuyruk on yedi kalem uzunken doğru okuma "sıradaki işi
   * kuyrukta bitirmek". Satırın içindeki düğme bunu 1 tık yapıyor. Sipariş bağlantısı
   * duruyor: bağlam gerektiğinde hâlâ oraya gidilebiliyor.
   *
   * Düğme yalnız uç `verifiableByMe: true` dediğinde etkin. Kural uçta canlı
   * (`workflow.service.ts`, dört göz); ekran onu tekrar etmiyor, uçtan gelen cevabı çiziyor.
   */
  eylem: {
    sutunAdi: 'Eylem',
    dogrula: 'Doğrula',
    dogrulaniyor: 'Doğrulanıyor…',
    /** Neden basılamıyor: düğme gizlenmiyor, gerekçesi yazılıyor. */
    dogrulanamaz: 'Kendi kaydettiğiniz ödemeyi siz doğrulayamazsınız; ikinci bir yetkili doğrulayacak.',
    tutarOkunamadi: 'Bu satırın tutarı görünmüyor; doğrulama finans yetkisi istiyor.',
    dogrulandi: (tutar: string) => `Ödeme kanıtı doğrulandı: ${tutar}`,
    zatenDogrulandi: 'Zaten doğrulanmış; yineleme fazladan hiçbir tutar yazmadı.',
  },

  bolum: {
    ustBaslik: 'BEKLEYEN İŞ VE YAŞI',
    /** Rozette gecikme eşiği ve o eşiği aşan iş sayısı: "48s+ bekleyen: 3". */
    gecikmeEki: 's+ bekleyen:',
    kuyruklarOkunuyor: 'Kuyruklar okunuyor…',
    bant1: 'Bekleyen iş',
    bant2: 'İkinci sıra kuyruklar',
    bant3: 'Durum',
    okunmaAni: 'Okunma anı:',
  },

  bos: {
    baslik: 'Pano',
    tarif:
      'Bu rol hiçbir bölüme atanmadı, o yüzden hiçbir bölümün panosunu görmüyor. Rol yalnız denetim ve devir amaçlıdır.',
  },
} as const;
